import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import {
  ChevronLeft, Phone, MessageCircle, Navigation, Clock,
  MapPin, Package, Search, AlertTriangle, ExternalLink, ShoppingCart, Send, FileText, Plus, Minus, Trash2, Download, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { feeForZone } from "../lib/deliveryPricing";
import CnomUsurpationNotice from "../components/CnomUsurpationNotice";
import PurchasePaymentModal from "../components/PurchasePaymentModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

// Custom marker icon
const pharmacyIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const PharmacyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation("public");
  const [searchParams] = useSearchParams();
  const searchedMedicine = searchParams.get('q') || searchParams.get('medicine') || '';
  
  const [pharmacy, setPharmacy] = useState(null);
  const [stock, setStock] = useState([]);
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [deliveryRequested, setDeliveryRequested] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryZoneId, setDeliveryZoneId] = useState("");
  const [deliveryPricing, setDeliveryPricing] = useState({ fallback_price: 0, zones: [], currency: "CDF" });
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Proforma cart state
  const [proformaCart, setProformaCart] = useState([]);
  const [showProformaModal, setShowProformaModal] = useState(false);
  const [proformaClientName, setProformaClientName] = useState("");
  const [proformaClientPhone, setProformaClientPhone] = useState("");
  const [generatingProforma, setGeneratingProforma] = useState(false);
  const [lastProformaId, setLastProformaId] = useState(null);
  const [paymentPurchase, setPaymentPurchase] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchPharmacyDetails();
  }, [id]);

  useEffect(() => {
    apiClient.get("/delivery/pricing")
      .then((res) => setDeliveryPricing(res.data || { fallback_price: 0, zones: [] }))
      .catch(() => {});
  }, []);

  const fetchPharmacyDetails = async () => {
    try {
      const response = await apiClient.get(`/pharmacies/${id}`);
      setPharmacy(response.data.pharmacy);
      setStock(response.data.stock);
      setIsOpen(response.data.is_open);
    } catch (error) {
      toastApiError(error);
      navigate("/search");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error(t("loginToPurchase"));
      navigate("/login");
      return;
    }

    if (deliveryRequested && !deliveryAddress.trim()) {
      toast.error(t("deliveryAddressRequired"));
      return;
    }

    const qty = parseInt(purchaseQuantity, 10);
    if (!qty || qty < 1) {
      toast.error(t("invalidQuantity"));
      return;
    }
    if (selectedMedicine?.quantity && qty > selectedMedicine.quantity) {
      toast.error(t("quantityExceedsStock"));
      return;
    }

    setPurchasing(true);
    try {
      const res = await apiClient.post("/purchases/request", {
        pharmacy_id: id,
        medicine_id: selectedMedicine.medicine.id,
        quantity: qty,
        delivery_requested: deliveryRequested,
        delivery_address: deliveryRequested ? deliveryAddress : null,
        delivery_zone_id: deliveryRequested ? (deliveryZoneId || null) : null,
      });
      toast.success(res.data?.message || t("orderRegistered"));
      setShowPurchaseModal(false);
      setPaymentPurchase({
        purchase_id: res.data.purchase_id,
        medicine_name: res.data.medicine_name || selectedMedicine?.medicine?.name,
        grand_total: res.data.grand_total ?? (
          (selectedMedicine?.price || 0) * qty
          + (deliveryRequested ? feeForZone(deliveryPricing, deliveryZoneId).fee : 0)
        ),
      });
      setShowPaymentModal(true);
      setSelectedMedicine(null);
      setPurchaseQuantity(1);
      setDeliveryRequested(false);
      setDeliveryAddress("");
      setDeliveryZoneId("");
    } catch (error) {
      toastApiError(error);
    } finally {
      setPurchasing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!user) {
      toast.error(t("loginToMessage"));
      navigate("/login");
      return;
    }

    if (!messageContent.trim()) {
      toast.error(t("writeMessage"));
      return;
    }

    setSendingMessage(true);
    try {
      const response = await apiClient.post("/messages/send", {
        recipient_id: id,
        content: messageContent
      });
      toast.success(t("messageSent"));
      setShowMessageModal(false);
      setMessageContent("");
      navigate(`/messages/${response.data.conversation_id}`);
    } catch (error) {
      toastApiError(error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Proforma cart functions
  const addToProformaCart = (item) => {
    const existingIndex = proformaCart.findIndex(c => c.medicine_id === item.medicine.id);
    
    if (existingIndex >= 0) {
      // Check stock
      if (proformaCart[existingIndex].quantity >= item.quantity) {
        toast.error(t("insufficientStock"));
        return;
      }
      const newCart = [...proformaCart];
      newCart[existingIndex].quantity += 1;
      setProformaCart(newCart);
    } else {
      setProformaCart([...proformaCart, {
        medicine_id: item.medicine.id,
        medicine_name: item.medicine.name,
        quantity: 1,
        unit_price: item.price,
        max_stock: item.quantity
      }]);
    }
    toast.success(t("addedToProforma", { name: item.medicine.name }));
  };

  const updateProformaQuantity = (index, delta) => {
    const newCart = [...proformaCart];
    const newQuantity = newCart[index].quantity + delta;
    
    if (newQuantity <= 0) {
      newCart.splice(index, 1);
    } else if (newQuantity > newCart[index].max_stock) {
      toast.error(t("insufficientStock"));
      return;
    } else {
      newCart[index].quantity = newQuantity;
    }
    
    setProformaCart(newCart);
  };

  const removeFromProformaCart = (index) => {
    const newCart = [...proformaCart];
    newCart.splice(index, 1);
    setProformaCart(newCart);
  };

  const calculateProformaTotal = () => {
    return proformaCart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleGenerateProforma = async () => {
    if (proformaCart.length === 0) {
      toast.error(t("addProformaProducts"));
      return;
    }

    setGeneratingProforma(true);
    try {
      const proformaData = {
        pharmacy_id: id,
        items: proformaCart.map(item => ({
          medicine_id: item.medicine_id,
          medicine_name: item.medicine_name,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        client_name: proformaClientName || null,
        client_phone: proformaClientPhone || null
      };

      const res = await apiClient.post("/proforma/create", proformaData);
      setLastProformaId(res.data.id);
      toast.success(t("proformaGenerated"));
      
      // Open PDF in new tab
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/proforma/${res.data.id}/pdf`, '_blank');
      
      // Reset
      setProformaCart([]);
      setProformaClientName("");
      setProformaClientPhone("");
      setShowProformaModal(false);
    } catch (error) {
      toastApiError(error);
    } finally {
      setGeneratingProforma(false);
    }
  };

  // Filter stock
  const filteredStock = stock.filter((item) => {
    const matchesSearch =
      item.medicine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.medicine.molecule?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || item.medicine.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Sort stock to show searched medicine first
  const sortedStock = [...filteredStock].sort((a, b) => {
    if (!searchedMedicine) return 0;
    
    const searchLower = searchedMedicine.toLowerCase();
    const aName = a.medicine.name.toLowerCase();
    const bName = b.medicine.name.toLowerCase();
    const aMolecule = (a.medicine.molecule || '').toLowerCase();
    const bMolecule = (b.medicine.molecule || '').toLowerCase();
    
    // Exact match first
    const aExact = aName === searchLower || aMolecule === searchLower;
    const bExact = bName === searchLower || bMolecule === searchLower;
    if (aExact && !bExact) return -1;
    if (bExact && !aExact) return 1;
    
    // Contains match second
    const aContains = aName.includes(searchLower) || aMolecule.includes(searchLower);
    const bContains = bName.includes(searchLower) || bMolecule.includes(searchLower);
    if (aContains && !bContains) return -1;
    if (bContains && !aContains) return 1;
    
    return 0;
  });

  // Get unique categories
  const categories = [...new Set(stock.map((item) => item.medicine.category).filter(Boolean))];

  // Format opening hours
  const formatHours = (hours) => {
    if (!hours || Object.keys(hours).length === 0) {
      return t("hoursNotProvided");
    }
    const days = {
      monday: t("dayMon"),
      tuesday: t("dayTue"),
      wednesday: t("dayWed"),
      thursday: t("dayThu"),
      friday: t("dayFri"),
      saturday: t("daySat"),
      sunday: t("daySun")
    };
    return Object.entries(hours).map(([day, times]) => (
      <div key={day} className="flex justify-between text-sm">
        <span className="text-slate-600">{days[day] || day}</span>
        <span className="font-medium">
          {times.open} - {times.close}
        </span>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omu-blue-500"></div>
      </div>
    );
  }

  if (!pharmacy) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-900">{t("pharmacyNotFound")}</h2>
          <Button className="mt-4" onClick={() => navigate("/search")}>
            {t("backToSearch")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <Link to="/">
              <img src={LOGO_URL} alt="OMU" className="h-16 w-auto" />
            </Link>

            <div className="flex-1">
              <h1 className="font-heading font-semibold text-slate-900 truncate">
                {pharmacy.entity_name}
              </h1>
            </div>

            <Badge
              className={`${
                isOpen ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              {isOpen ? t("open") : t("closed")}
            </Badge>
          </div>
        </div>
      </header>

      <div className="bg-amber-50 border-b border-amber-200 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{t("confirmAvailabilityDisclaimer")}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Card - if pharmacy has photo */}
            {pharmacy.photo_url && (
              <Card className="border-0 shadow-soft overflow-hidden" data-testid="pharmacy-photo-card">
                <img 
                  src={pharmacy.photo_url} 
                  alt={pharmacy.entity_name}
                  className="w-full h-48 sm:h-64 object-cover"
                />
              </Card>
            )}

            {/* Info Card */}
            <Card className="border-0 shadow-soft" data-testid="pharmacy-info-card">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Small photo thumbnail if no big photo */}
                    {!pharmacy.photo_url && pharmacy.photo_url && (
                      <img 
                        src={pharmacy.photo_url} 
                        alt={pharmacy.entity_name}
                        className="w-16 h-16 rounded-xl object-cover"
                      />
                    )}
                    <div>
                      <h2 className="font-heading text-2xl font-bold text-slate-900">
                        {pharmacy.entity_name}
                      </h2>
                      <Badge variant="outline" className="mt-2 capitalize">
                        {pharmacy.entity_type === "autre"
                          ? pharmacy.entity_type_other
                          : pharmacy.entity_type}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-omu-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-slate-900">{pharmacy.address}</p>
                      <p className="text-slate-500">
                        {pharmacy.commune && `${pharmacy.commune}, `}
                        {pharmacy.city}, {pharmacy.country}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-omu-blue-500 flex-shrink-0" />
                    <a
                      href={`tel:${pharmacy.phone}`}
                      className="text-slate-900 hover:text-omu-blue-500"
                    >
                      {pharmacy.phone}
                    </a>
                  </div>

                  {pharmacy.whatsapp && (
                    <div className="flex items-center gap-3">
                      <MessageCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <a
                        href={`https://wa.me/${pharmacy.whatsapp.replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-900 hover:text-green-500"
                      >
                        {pharmacy.whatsapp}
                      </a>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    className="flex-1 sm:flex-none rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                    onClick={() => (window.location.href = `tel:${pharmacy.phone}`)}
                    data-testid="call-btn"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {t("call")}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none rounded-full border-green-200 text-green-600 hover:bg-green-50"
                    onClick={() => setShowMessageModal(true)}
                    data-testid="message-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {t("contact")}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none rounded-full"
                    onClick={() => {
                      if (pharmacy.latitude && pharmacy.longitude) {
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${pharmacy.latitude},${pharmacy.longitude}`,
                          "_blank"
                        );
                      } else if (pharmacy.address && pharmacy.city) {
                        const address = encodeURIComponent(`${pharmacy.address}, ${pharmacy.commune || ""}, ${pharmacy.city}, RDC`);
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${address}`,
                          "_blank"
                        );
                      } else {
                        toast.error(t("addressUnavailable"));
                      }
                    }}
                    data-testid="directions-btn"
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    {t("directions")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stock Card */}
            <Card className="border-0 shadow-soft" data-testid="pharmacy-stock-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl">
                    <Package className="inline h-5 w-5 mr-2 text-omu-blue-500" />
                    {t("availableMedicines", { count: sortedStock.length })}
                  </CardTitle>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder={t("searchInStock")}
                      className="pl-10 rounded-xl"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="stock-search-input"
                    />
                  </div>
                  <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                    <TabsList className="bg-slate-100 overflow-x-auto">
                      <TabsTrigger value="all">{t("allCategories")}</TabsTrigger>
                      {categories.slice(0, 4).map((cat) => (
                        <TabsTrigger key={cat} value={cat} className="capitalize">
                          {cat}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent>
                {sortedStock.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-10 w-10 mx-auto text-slate-300" />
                    <p className="mt-2 text-slate-500">{t("noMedicineInStock")}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sortedStock.map((item, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                          item.medicine.requires_prescription 
                            ? 'bg-red-50 hover:bg-red-100 border border-red-200' 
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                        data-testid={`stock-item-${idx}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-slate-900">
                              {item.medicine.name}
                              {item.medicine.brand && item.medicine.brand !== item.medicine.name && (
                                <span className="text-slate-400 text-sm ml-2">({item.medicine.brand})</span>
                              )}
                            </h4>
                            {item.medicine.requires_prescription && (
                              <Badge className="bg-red-100 text-red-700 text-xs">
                                <FileText className="h-3 w-3 mr-1" />
                                {t("prescriptionRequired")}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {item.medicine.dosage} • {item.medicine.form}
                            {item.medicine.molecule && ` • ${item.medicine.molecule}`}
                          </p>
                        </div>
                        <div className="text-right flex items-center gap-3">
                          <div>
                            <Badge
                              className={`${
                                item.quantity > 10
                                  ? "bg-green-100 text-green-700"
                                  : item.quantity > 0
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {t("units", { count: item.quantity })}
                            </Badge>
                            {item.price && (
                              <p className="text-sm text-omu-blue-500 font-medium mt-1">
                                {item.price.toLocaleString()} CDF
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {item.quantity > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full border-omu-blue-200 text-omu-blue-600 hover:bg-omu-blue-50"
                                onClick={() => addToProformaCart(item)}
                                title={t("addToProformaTitle")}
                                data-testid={`proforma-btn-${idx}`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {user?.role === "client" && item.quantity > 0 && (
                              <Button
                                size="sm"
                                className="rounded-full bg-green-500 hover:bg-green-600"
                                onClick={() => {
                                  setSelectedMedicine(item);
                                  setShowPurchaseModal(true);
                                }}
                                data-testid={`buy-btn-${idx}`}
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Map Card */}
            {pharmacy.latitude && pharmacy.longitude && (
              <Card className="border-0 shadow-soft overflow-hidden" data-testid="pharmacy-map-card">
                <div className="h-48">
                  <MapContainer
                    center={[pharmacy.latitude, pharmacy.longitude]}
                    zoom={15}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                    zoomControl={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[pharmacy.latitude, pharmacy.longitude]}
                      icon={pharmacyIcon}
                    >
                      <Popup>{pharmacy.entity_name}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
                <CardContent className="p-4">
                  <Button
                    className="w-full rounded-full"
                    variant="outline"
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${pharmacy.latitude},${pharmacy.longitude}`,
                        "_blank"
                      )
                    }
                    data-testid="view-map-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t("viewOnGoogleMaps")}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Opening Hours Card */}
            <Card className="border-0 shadow-soft" data-testid="pharmacy-hours-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-omu-blue-500" />
                  {t("openingHours")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">{formatHours(pharmacy.opening_hours)}</div>
              </CardContent>
            </Card>

            {/* Services Card */}
            {pharmacy.services && pharmacy.services.length > 0 && (
              <Card className="border-0 shadow-soft" data-testid="pharmacy-services-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">{t("services")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {pharmacy.services.map((service, idx) => (
                      <Badge key={idx} variant="outline">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("purchaseRequestTitle")}</DialogTitle>
            <DialogDescription>
              {selectedMedicine?.medicine?.requires_prescription
                ? t("purchasePrescriptionHint")
                : t("purchasePaymentHint")}
            </DialogDescription>
          </DialogHeader>
          {selectedMedicine && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-medium">{selectedMedicine.medicine.name}</h4>
                <p className="text-sm text-slate-500">
                  {selectedMedicine.medicine.dosage} • {selectedMedicine.medicine.form}
                </p>
                {selectedMedicine.price && (
                  <p className="text-omu-blue-500 font-medium mt-1">
                    {t("perUnit", { price: selectedMedicine.price.toLocaleString() })}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="quantity">{t("quantity")}</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  max={selectedMedicine.quantity}
                  value={purchaseQuantity}
                  onChange={(e) => setPurchaseQuantity(parseInt(e.target.value) || 1)}
                  className="rounded-xl"
                />
                <p className="text-sm text-slate-500">
                  {t("available", { count: selectedMedicine.quantity })}
                </p>
              </div>

              {selectedMedicine.price && (
                <div className="bg-omu-blue-50 p-4 rounded-xl space-y-1">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>{t("products")}</span>
                    <span>{(selectedMedicine.price * purchaseQuantity).toLocaleString()} CDF</span>
                  </div>
                  {deliveryRequested && (
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>{t("delivery", { zone: feeForZone(deliveryPricing, deliveryZoneId).zoneName })}</span>
                      <span>{feeForZone(deliveryPricing, deliveryZoneId).fee.toLocaleString()} CDF</span>
                    </div>
                  )}
                  <p className="text-xl font-bold text-omu-blue-600 pt-1">
                    {(
                      selectedMedicine.price * purchaseQuantity
                      + (deliveryRequested ? feeForZone(deliveryPricing, deliveryZoneId).fee : 0)
                    ).toLocaleString()} CDF
                  </p>
                </div>
              )}

                {selectedMedicine.medicine.requires_prescription && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    {t("prescriptionOrderNote")}
                  </p>
                )}

              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={deliveryRequested}
                  onChange={(e) => setDeliveryRequested(e.target.checked)}
                />
                <span>
                  <Trans i18nKey="deliveryCheckbox" ns="public" components={{ strong: <strong /> }} />
                </span>
              </label>
              {deliveryRequested && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="delivery-zone">{t("deliveryZone")}</Label>
                    <select
                      id="delivery-zone"
                      className="w-full h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm"
                      value={deliveryZoneId}
                      onChange={(e) => setDeliveryZoneId(e.target.value)}
                    >
                      <option value="">
                        {t("deliveryZoneDefault", { price: (deliveryPricing.fallback_price || 0).toLocaleString() })}
                      </option>
                      {(deliveryPricing.zones || []).map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name} — {Number(zone.price).toLocaleString()} CDF
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery-address">{t("deliveryAddress")}</Label>
                    <Input
                      id="delivery-address"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder={t("deliveryAddressPlaceholder")}
                      className="rounded-xl"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {selectedMedicine?.medicine?.requires_prescription && (
            <div className="mt-1 mb-2">
              <CnomUsurpationNotice />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseModal(false)}>
              {t("cancel")}
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600"
              onClick={handlePurchase}
              disabled={purchasing}
            >
              {purchasing ? t("sending") : t("confirmPurchase")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PurchasePaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        purchase={paymentPurchase}
        onSuccess={() => navigate("/purchases")}
      />

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("contactPharmacy", { name: pharmacy?.entity_name })}</DialogTitle>
            <DialogDescription>
              {t("sendMessageToPharmacy")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="message">{t("yourMessage")}</Label>
              <textarea
                id="message"
                className="w-full h-32 p-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-omu-blue-500"
                placeholder={t("messagePlaceholder")}
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageModal(false)}>
              {t("cancel")}
            </Button>
            <Button 
              className="bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={handleSendMessage}
              disabled={sendingMessage || !messageContent.trim()}
            >
              {sendingMessage ? t("sending") : t("send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proforma Modal */}
      <Dialog open={showProformaModal} onOpenChange={setShowProformaModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-omu-blue-500" />
              {t("generateProforma")}
            </DialogTitle>
            <DialogDescription>
              {t("proformaDescription")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="proforma-name" className="text-sm">{t("nameOptional")}</Label>
                <Input
                  id="proforma-name"
                  placeholder={t("yourName")}
                  value={proformaClientName}
                  onChange={(e) => setProformaClientName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="proforma-phone" className="text-sm">{t("phoneOptional")}</Label>
                <Input
                  id="proforma-phone"
                  placeholder="08xxxxxxxx"
                  value={proformaClientPhone}
                  onChange={(e) => setProformaClientPhone(e.target.value)}
                />
              </div>
            </div>

            {/* Cart Items */}
            <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
              {proformaCart.map((item, i) => (
                <div key={i} className="p-3 flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.medicine_name}</p>
                    <p className="text-sm text-slate-500">
                      {item.unit_price.toLocaleString()} CDF x {item.quantity}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateProformaQuantity(i, -1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateProformaQuantity(i, 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500"
                      onClick={() => removeFromProformaCart(i)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                    <p className="font-semibold text-right w-24">
                      {(item.unit_price * item.quantity).toLocaleString()} CDF
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex justify-between items-center p-4 bg-omu-blue-50 rounded-lg">
              <span className="text-lg font-semibold">{t("estimatedTotal")}</span>
              <span className="text-2xl font-bold text-omu-blue-600">
                {calculateProformaTotal().toLocaleString()} CDF
              </span>
            </div>

            <p className="text-xs text-slate-500 text-center">
              {t("proformaDisclaimer")}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProformaModal(false)}>
              {t("cancel")}
            </Button>
            <Button 
              className="bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={handleGenerateProforma}
              disabled={generatingProforma || proformaCart.length === 0}
            >
              {generatingProforma ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t("downloadPdf")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Proforma Cart Button */}
      {proformaCart.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            className="h-14 px-6 bg-omu-blue-500 hover:bg-omu-blue-600 shadow-lg rounded-full"
            onClick={() => setShowProformaModal(true)}
            data-testid="proforma-cart-btn"
          >
            <FileText className="h-5 w-5 mr-2" />
            {t("proformaCart", { count: proformaCart.length })}
            <Badge className="ml-2 bg-white text-omu-blue-600">
              {calculateProformaTotal().toLocaleString()} CDF
            </Badge>
          </Button>
        </div>
      )}
    </div>
  );
};

export default PharmacyDetailsPage;
